/**
 * MelSearchInput Component
 * Search input for MEL lookup with placeholder for future voice input
 */

import React, { useState, useCallback } from 'react';
import { cn } from '@/utils/cn';
import {
  MagnifyingGlassIcon,
  MicrophoneIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

interface MelSearchInputProps {
  value: string;
  onChange: (value: string) => void;
  onSearch: () => void;
  placeholder?: string;
  isLoading?: boolean;
  disabled?: boolean;
  className?: string;
}

export const MelSearchInput: React.FC<MelSearchInputProps> = ({
  value,
  onChange,
  onSearch,
  placeholder = 'Describe the issue (e.g., "left landing light inop", "APU won\'t start")',
  isLoading = false,
  disabled = false,
  className,
}) => {
  const [isFocused, setIsFocused] = useState(false);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' && !disabled && value.trim()) {
        onSearch();
      }
    },
    [onSearch, disabled, value]
  );

  const handleClear = useCallback(() => {
    onChange('');
  }, [onChange]);

  const handleVoiceClick = useCallback(() => {
    // Placeholder for future voice input functionality
    alert('Voice input coming soon! For now, please type your query.');
  }, []);

  return (
    <div className={cn('relative', className)}>
      <div
        className={cn(
          'flex items-center gap-2 px-4 py-3 rounded-lg border transition-all duration-200',
          'bg-background',
          isFocused
            ? 'border-primary ring-2 ring-primary/20'
            : 'border-border hover:border-primary/50',
          disabled && 'opacity-50 cursor-not-allowed'
        )}
      >
        {/* Search Icon */}
        <MagnifyingGlassIcon
          className={cn(
            'w-5 h-5 flex-shrink-0 transition-colors',
            isFocused ? 'text-primary' : 'text-foreground/40'
          )}
        />

        {/* Input */}
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          placeholder={placeholder}
          disabled={disabled || isLoading}
          className={cn(
            'flex-1 bg-transparent outline-none text-foreground',
            'placeholder:text-foreground/40',
            'disabled:cursor-not-allowed'
          )}
        />

        {/* Clear button */}
        {value && !isLoading && (
          <button
            type="button"
            onClick={handleClear}
            disabled={disabled}
            className={cn(
              'p-1 rounded-md transition-colors',
              'text-foreground/40 hover:text-foreground hover:bg-muted',
              'disabled:opacity-50 disabled:cursor-not-allowed'
            )}
            aria-label="Clear search"
          >
            <XMarkIcon className="w-4 h-4" />
          </button>
        )}

        {/* Voice input button (placeholder) */}
        <button
          type="button"
          onClick={handleVoiceClick}
          disabled={disabled || isLoading}
          className={cn(
            'p-2 rounded-md transition-colors',
            'text-foreground/40 hover:text-primary hover:bg-primary/10',
            'disabled:opacity-50 disabled:cursor-not-allowed'
          )}
          aria-label="Voice input (coming soon)"
          title="Voice input coming soon"
        >
          <MicrophoneIcon className="w-5 h-5" />
        </button>

        {/* Search button */}
        <button
          type="button"
          onClick={onSearch}
          disabled={disabled || isLoading || !value.trim()}
          className={cn(
            'px-4 py-2 rounded-md font-medium text-sm transition-all duration-200',
            'bg-primary text-primary-foreground',
            'hover:bg-primary-hover active:bg-primary-active',
            'disabled:opacity-50 disabled:cursor-not-allowed',
            isLoading && 'cursor-wait'
          )}
        >
          {isLoading ? (
            <span className="flex items-center gap-2">
              <svg
                className="animate-spin w-4 h-4"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              Searching...
            </span>
          ) : (
            'Search'
          )}
        </button>
      </div>

      {/* Helper text */}
      <p className="mt-2 text-xs text-foreground/50">
        Enter a description of the equipment issue to find the applicable MEL item
      </p>
    </div>
  );
};

export default MelSearchInput;
